import { Link } from "react-router";
import cx from "../../../../../../lib/cx.js";
import { ModuleTime } from "./ModuleTime.jsx";
import { ModuleTitle } from "./ModuleTitle.jsx";
import { styles } from "./ModuleLink.css.js";

/**
 * Constructs the absolute URL for a given module based on its type and properties.
 *
 * @param {Object} mod - The module object for which the URL is to be generated.
 * @param {string} mod.type - The type of the module, determining the URL structure.
 * @param {Array} [mod.steps] - Array of steps if the module type is "station".
 * @param {string} mod.steps[].slug - Slug of the first step in the module.
 * @param {string} mod.slug - Slug of the module.
 * @param {string} baseUrl - The spark's base URL (e.g. "/w/<ws>/s/<spark>"),
 *   built via `ROUTES.spark.getUrl(...)`. The step path is appended to it so the
 *   link is absolute and independent of the current location.
 * @returns {string} - The absolute URL for the provided module.
 */
const getModuleLink = (mod, baseUrl) => {
  if (mod.type === "station") {
    if (mod.steps?.length === 0) return "";
    return `${baseUrl}/step/${mod.steps[0].slug}`;
  } else {
    return `${baseUrl}/step/${mod.slug}`;
  }
};
/**
 * Retrieves the URLs of preview images for a given module.
 *
 * This function processes modules of different types to extract and return
 * an array of unique preview image URLs.
 *
 * @param {Object} mod - The module object containing information about the module.
 * @param {string} mod.type - The type of the module (e.g., "station").
 * @param {Array} [mod.steps] - An array of steps relevant to the module, applicable if the module is of type "station".
 * @param {string} [mod.previewImageUrl] - The URL of the module's preview image, applicable if the module is not of type "station".
 * @returns {string[]} An array of unique preview image URLs.
 */
const getModuleImgs = (mod) => {
  if (mod.type === "station") {
    return [
      ...new Set(mod.steps.map((s) => s.previewImageUrl).filter(Boolean)),
    ];
  } else {
    return mod.previewImageUrl ? [mod.previewImageUrl] : [];
  }
};

/**
 * Represents a row in the module list - for selecting which module to view in the Spark.
 *
 * @typedef {Object} ModuleLink
 * @property {object} mod - The module object.
 * @property {string} baseUrl - The spark's base URL the step path is appended to.
 */
export const ModuleLink = ({ mod, baseUrl, disableSome = false }) => {
  const imgs = getModuleImgs(mod);
  const moduleUrl = getModuleLink(mod, baseUrl);
  const disabled = disableSome && mod.disabled;

  const duration = mod.recommendedMinutes;

  const className = cx(
    styles.moduleLink.base,
    disabled && styles.moduleLink.disabled,
  );
  const content = (
    <>
      <ModuleTime duration={duration} />

      <ModuleTitle title={mod.title} imgs={imgs} />
    </>
  );

  if (disabled) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Link
      to={moduleUrl}
      className={className}
      data-cy="cy-workshopansicht-modul-link"
    >
      {content}
    </Link>
  );
};
